import React, { useCallback, useEffect, useState } from "react";
import { DateHelper, StringHelper } from "@bryntum/schedulerpro";
import { PLUGINS_KEYS } from "@/other/constants/PluginKeys";
import { TIMELINE_CONG_KEYS } from "@/other/constants/OtherKeys";
import { getScheduleInstanceFromViewManager } from "@/other/utils/otherUtils";
import { SwivuList } from "@/modern/components/swivu-list";
import { TimePicker } from "@/modern/components/time-picker/time-picker";
import { Switch } from "@/modern/components/ui/switch";
import { Button } from "@/modern/components/ui/button";
import { NonWorkingTime } from "@/bryntum-lib/models/NonWorkingTime";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import Combobox from "@/modern/components/ui/combobox";
import { toast } from "sonner";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Plus } from "lucide-react";

const weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];


const NonWorkingTimeDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, viewManager } = useSwivuBryntumContext();
  const [nonWorkingTimes, setNonWorkingTimes] = useState<NonWorkingTime[]>([]);
  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.nonWorkingTimeStore,
  })
  const schedulerView = getScheduleInstanceFromViewManager(viewManager);

  const refreshNonWorkingTimes = useCallback(() => {
    if (!project) return;
    const nonWorkingTimes = project.nonWorkingTimeStore.query(() => true, true);
    setNonWorkingTimes(nonWorkingTimes);
  }, [project]);


  useEffect(() => {
    refreshNonWorkingTimes();
    return subscribe(() => {
      refreshNonWorkingTimes();
      persistSettings();
    })
  }, [subscribe, refreshNonWorkingTimes]);

  const persistSettings = () => {
    if (schedulerView) {
      const nonWorkingTimeRecords = project.nonWorkingTimeStore.query(() => true).map((r: any) => r.id);
      schedulerView.features[PLUGINS_KEYS.swivuTimelineConfigurator]?.swivuExecuteSettings(TIMELINE_CONG_KEYS.nonWorkingTimeChecks, nonWorkingTimeRecords);
    }
  };

  const onAddNonWorking = () => {
    if (project) {
      const id = StringHelper.generateUUID();
      // Create default break time from 1PM to 2PM
      const beginTime = new Date();
      beginTime.setHours(13, 0, 0, 0);

      const endTime = new Date();
      endTime.setHours(14, 0, 0, 0);

      project.nonWorkingTimeStore.add({
        id,
        dayId: 0,
        allDay: false,
        beginTime,
        endTime
      });
    }
  };

  const onRemoveNonWorking = (record: NonWorkingTime) => {
    if (project) {
      // @ts-expect-error - isDefault is a 'field' in the model
      if (record?.isDefault) {
        toast.error("Cannot delete the default non working time records.");
      } else {
        project.nonWorkingTimeStore.remove(record);
      }
    }
  };

  const onAllDayChange = ({ record, checked }: { record: NonWorkingTime, checked: boolean }) => {
    if (!record.dayName) return;
    record.set("allDay", checked);
  };

  const handleTimeChange = ({ record, field, value }: { record: NonWorkingTime, field: string, value: Date | undefined }): boolean => {
    if (!value) {
      record.set(field, null);
      return true;
    }

    const timeValue = DateHelper.format(value, "HH:mm");

    // Validate time constraints
    if (field === "beginTime") {
      // When setting beginTime, ensure it's less than endTime (if endTime exists)
      if (record.endTime) {
        const endTime = DateHelper.format(record.endTime, "HH:mm");
        if (timeValue >= endTime) {
          toast.error("Begin time must be less than end time.");
          return false;
        }
      }
    } else if (field === "endTime") {
      // When setting endTime, ensure it's greater than beginTime (if beginTime exists)
      if (record.beginTime) {
        const beginTime = DateHelper.format(record.beginTime, "HH:mm");
        if (timeValue <= beginTime) {
          toast.error("End time must be greater than begin time.");
          return false;
        }
      }
    }

    record.set(field, value);
    return true;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="max-w-6xl">
        <SwivuList.Root<NonWorkingTime> items={nonWorkingTimes}>
          <SwivuList.Header>
            <SwivuList.HeaderItem flex>
              Day of the Week
            </SwivuList.HeaderItem>
            <SwivuList.HeaderItem flex>
              All Day
            </SwivuList.HeaderItem>
            <SwivuList.HeaderItem flex>
              Begin
            </SwivuList.HeaderItem>
            <SwivuList.HeaderItem flex>
              End
            </SwivuList.HeaderItem>
            <SwivuList.HeaderItem className="w-8">
              <span className="sr-only">
                Actions
              </span>
            </SwivuList.HeaderItem>
          </SwivuList.Header>

          <SwivuList.Content<NonWorkingTime> emptyMessage="No non-working times configured. Add your first one!">
            {(item: NonWorkingTime) => (
              <SwivuList.Item value={item.dayName}>
                <div className='flex-1 flex items-center gap-4'>
                  <Combobox
                    /* @ts-expect-error - isDefault is a 'field' in the model */
                    disabled={item.isDefault}
                    popoverContentClassName="w-max"
                    buttonSelectedClassName="text-sm bg-transparent p-0 !h-auto border-none w-auto min-w-0 hover:bg-transparent"
                    value={item.dayName}
                    onValueChange={(value) => {
                      item.set("dayName", value);
                    }}
                    options={weekDays.map((day) => ({ label: day, value: day }))}
                    placeholder="Select Day"
                  />
                </div>

                <div className="flex-1">
                  {/* @ts-expect-error - allDay is a 'field' in the model */}
                  <Switch checked={item.allDay} onCheckedChange={(checked: boolean) => {
                    onAllDayChange({ record: item, checked });
                  }} />
                </div>

                {/* @ts-expect-error - allDay is a 'field' in the model */}
                <TimePicker disabled={item.allDay} className="flex-1" date={item.beginTime || undefined} onChange={(date: Date | undefined) => {
                  return handleTimeChange({ record: item, field: "beginTime", value: date });
                }} />
                {/* @ts-expect-error - allDay is a 'field' in the model */}
                <TimePicker disabled={item.allDay} className="flex-1" date={item.endTime || undefined} onChange={(date: Date | undefined) => {
                  return handleTimeChange({ record: item, field: "endTime", value: date });
                }} />

                {/* @ts-expect-error - isDefault is a 'field' in the model */}
                <SwivuList.ItemActions disabled={item.isDefault} onDelete={() => onRemoveNonWorking(item)} />
              </SwivuList.Item>
            )}
          </SwivuList.Content>
        </SwivuList.Root>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={onAddNonWorking}>
            <Plus className="h-4 w-4" />
            Add Day
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NonWorkingTimeDetail;
